import { ref, reactive, onMounted, onUnmounted } from 'vue'
import { settle } from '@/utils/promises'
import { useEcho, createChannel, ECHO_EVENTS } from '@/echo'
import { usePoll } from '@/Composables/poll'
import { LANG, PERSIST_LOCK_MS, CONTEXT } from '@/Composables/Fulfillment/constants'
import { api } from '@/Pages/Fulfillment/lock-service-api'
import { useAccessCompany } from '@/Composables/access-company'
import { useAccessUser } from '@/Composables/access-user'
import { useErrors } from '@/Composables/errors'

export const useLockService = ({ order, fulfillmentService }) => {
  const { user } = useAccessUser()
  const { company } = useAccessCompany()
  const { showError } = useErrors()

  const { echoClient: echo } = useEcho()
  const FULFILLMENTS_CHANNEL = createChannel('fulfillments', company.value.id)

  const lockedModal = {
    state: reactive({
      isOpen: false,
      message: undefined
    }),

    open: (user, message) => {
      if (lockedModal.state.isOpen) {
        return
      }

      fulfillmentService.context.set(CONTEXT.LOCKED)

      if (message) {
        lockedModal.state.message = message
      } else if (user && user.first_name && user.last_name) {
        lockedModal.state.message = `${user.first_name} ${user.last_name} is currently editing this order. Take over?`
      } else {
        lockedModal.state.message = 'Another user is currently editing this order. Take over?'
      }
      lockedModal.state.isOpen = true
    },

    onClose: () => {
      if (!lockedModal.state.isOpen) {
        return
      }
      fulfillmentService.context.revert()
      lockedModal.state.isOpen = false
    },

    override: async () => {
      const [err, result] = await settle(api.acquireOrderLock(order.id, true))

      if (err) {
        lockedModal.onClose()
        await settle(showError({ message: LANG.ERROR_TAKING_OVER_LOCK }))
        return
      }

      const [status, data] = result

      if (status === 200 && data.user_id === user.value.id) {
        persistLockPoll.start()
        lockedModal.onClose()
        fulfillmentService.syncOrder()
      }
    }
  }

  const persistLock = async () => {
    console.log('🔐 Persisting the lock')

    const [err, result] = await settle(api.acquireOrderLock(order.id))

    hasLock.value = false

    if (err) {
      fulfillmentService.showHardError()
      return
    }

    const [status, json] = result

    if (status === 423) {
      persistLockPoll.stop()
      lockedModal.open(null, json.message)
      return
    }

    hasLock.value = true
  }

  const onLockChanged = (data) => {
    console.log('🔐 Received lock change event via WS.')

    if (data.id !== order.id) {
      return
    }

    if (data.is_locked && data.locked_by !== user.value.id) {
      persistLockPoll.stop()
      lockedModal.open(data.lock.user)
      return
    }
    if (!data.is_locked) {
      persistLockPoll.start()
      lockedModal.onClose()
      return
    }
  }

  const persistLockPoll = usePoll(persistLock, { interval: PERSIST_LOCK_MS, immediate: true })

  const hasLock = ref(false)

  if (!order.is_locked || (order.is_locked && order.locked_by === user.value.id)) {
    persistLockPoll.start()
  }

  if (order.is_locked && order.locked_by !== user.value.id) {
    lockedModal.open(order.lock.user)
  }

  onMounted(async () => {
    const channel = echo.private(FULFILLMENTS_CHANNEL)
    channel.listen(ECHO_EVENTS.ORDER_LOCK_CHANGED, onLockChanged)
  })

  onUnmounted(() => {
    echo.leave(FULFILLMENTS_CHANNEL)

    if (hasLock.value) {
      api.releaseOrderLock(order.id)
    }
  })

  return {
    lockedModal
  }
}
